import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnalysis, findFileInMcp } from '../../services/analyses';
import { downloadFile } from '../../services/folder';
import { getAppConfig } from '../../services/settings';
import ElementsSearchModal from './ElementsSearchPanel';
import ReferenceHistory from '../common/ReferenceHistory';
import './ComparisonView.css';

// Componente embebido de preview (versión simplificada para comparación)
const EmbeddedPreview = ({ analysis, title, titleColor, searchHash, imageUrl, showActions, onShowPreview, matchedPage }) => {
  const navigate = useNavigate();
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  const result = analysis?.result;
  const fullJson = result?.full_json || result || {};
  const tipoPieza = fullJson.TipoPieza || analysis?.metadata?.part_type || 'N/A';
  const invoiceData = fullJson._invoice_data || null;
  const isQueryPiece = analysis?.isQueryPiece || false;

  const [mcpPath, setMcpPath] = useState(() => {
    try {
      const metadata = analysis?.metadata_json ? JSON.parse(analysis.metadata_json) : {};
      return metadata.mcp_path || null;
    } catch (e) {
      return null;
    }
  });
  const [downloadSearching, setDownloadSearching] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    if (imageUrl) {
      setImageBlobUrl(imageUrl);
      return;
    }

    setImageBlobUrl(null);

    const loadImage = async () => {
      try {
        let url = searchHash
          ? `${process.env.REACT_APP_API_URL}/v1/analyses/search-similar/${searchHash}/image`
          : `${process.env.REACT_APP_API_URL}/v1/analyses/${analysis.hash}/image`;

        if (matchedPage) {
          url += `?page=${matchedPage}`;
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setImageBlobUrl(blobUrl);
        }
      } catch (error) {
        console.error('Error loading image:', error);
      }
    };

    if (searchHash || (analysis?.hash && analysis.hash !== 'query')) {
      loadImage();
    }

    return () => {
      if (!imageUrl && imageBlobUrl) {
        URL.revokeObjectURL(imageBlobUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis?.hash, searchHash, imageUrl, matchedPage]);

  if (!analysis) {
    return (
      <div className="embedded-preview empty">
        <p>Selecciona un resultado de la lista para comparar</p>
      </div>
    );
  }

  const handleDownloadOriginal = async () => {
    setDownloadError(null);
    try {
      let blob;
      if (mcpPath) {
        try { blob = await downloadFile(mcpPath); } catch { blob = null; }
      }
      if (!blob) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/v1/analyses/${analysis.hash}/original`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (response.ok) blob = await response.blob();
      }
      if (!blob && analysis.source === 'mcp') {
        setDownloadSearching(true);
        try {
          const result = await findFileInMcp(analysis.hash);
          if (result.found) {
            setMcpPath(result.new_path);
            blob = await downloadFile(result.new_path);
          }
        } finally {
          setDownloadSearching(false);
        }
      }
      if (!blob) {
        const msg = analysis.source === 'mcp'
          ? 'No se ha podido encontrar el archivo. Es posible que haya sido eliminado o movido fuera de la colección.'
          : 'No se ha podido encontrar el archivo original.';
        setDownloadError(msg);
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = analysis.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setDownloadError('Error al descargar el archivo.');
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/v1/analyses/${analysis.hash}/excel`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.ok) {
        const blob = await response.blob();
        const fecha = analysis.created ? new Date(analysis.created).toISOString().slice(0, 10).replace(/-/g, '') : '';
        const baseName = analysis.filename.replace(/\.[^/.]+$/, '');
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analisis_${baseName}_${fecha}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
    }
  };

  const handleGoToLocation = () => {
    if (mcpPath) {
      const folderPath = mcpPath.substring(0, mcpPath.lastIndexOf('/')) || '/';
      const fileName = mcpPath.substring(mcpPath.lastIndexOf('/') + 1);
      navigate(`/folder-browser?path=${encodeURIComponent(folderPath)}&highlight=${encodeURIComponent(fileName)}`);
    }
  };

  return (
    <div className="embedded-preview">
      <div className="embedded-header" style={{ borderLeftColor: titleColor }}>
        <h3>{title}</h3>
      </div>

      {(showActions || onShowPreview) && (
        <div className="embedded-actions">
          {onShowPreview && (
            <button onClick={onShowPreview} className="btn-embedded-action" title="Se abre en una pestaña nueva para no perder esta comparativa">
              Ver análisis completo ↗
            </button>
          )}
          {showActions && analysis.hash !== 'query' && (
            <>
              <button onClick={handleDownloadOriginal} className="btn-embedded-action" disabled={downloadSearching}>
                {downloadSearching ? 'Buscando archivo...' : 'Descargar archivo'}
              </button>
              {downloadError && <span className="download-error-msg">{downloadError}</span>}
              {analysis.status === 'completed' && (
                <button onClick={handleDownloadExcel} className="btn-embedded-action">
                  Descargar Excel
                </button>
              )}
              {analysis.source === 'mcp' && mcpPath && (
                <button onClick={handleGoToLocation} className="btn-embedded-action btn-location">
                  Ver ubicacion
                </button>
              )}
            </>
          )}
        </div>
      )}

      <div className="embedded-content">
        {/* Imagen del plano */}
        <div className="embedded-image-section">
          {imageBlobUrl ? (
            <div className="embedded-image" onClick={() => setImageModalOpen(true)}>
              <img src={imageBlobUrl} alt={analysis.filename} />
              <span className="zoom-hint">Click para ampliar</span>
            </div>
          ) : (
            <div className="embedded-image-loading">Cargando imagen...</div>
          )}
        </div>

        {/* Datos del documento */}
        <div className="embedded-operations">
          <h4>Datos del Documento</h4>
          {invoiceData ? (
            <div className="query-metadata-details">
              {(invoiceData.numero_documento || invoiceData.numero_pedido) && (
                <div className="metadata-detail-row">
                  <span className="label">Nº Documento:</span>
                  <span className="value">{invoiceData.numero_documento || invoiceData.numero_pedido}</span>
                </div>
              )}
              {invoiceData.fecha && (
                <div className="metadata-detail-row">
                  <span className="label">Fecha:</span>
                  <span className="value">{invoiceData.fecha}</span>
                </div>
              )}
              {invoiceData.cliente && (
                <div className="metadata-detail-row">
                  <span className="label">Cliente:</span>
                  <span className="value">{invoiceData.cliente}</span>
                </div>
              )}
              {invoiceData.proveedor && (
                <div className="metadata-detail-row">
                  <span className="label">Proveedor:</span>
                  <span className="value">{invoiceData.proveedor}</span>
                </div>
              )}
              {invoiceData.articulo && (
                <div className="metadata-detail-row">
                  <span className="label">Artículo:</span>
                  <span className="value">
                    <ReferenceHistory reference={invoiceData.articulo} />
                  </span>
                </div>
              )}
              {invoiceData.descripcion && (
                <div className="metadata-detail-row">
                  <span className="label">Descripción:</span>
                  <span className="value">{invoiceData.descripcion}</span>
                </div>
              )}
              {invoiceData.cantidad && (
                <div className="metadata-detail-row">
                  <span className="label">Cantidad:</span>
                  <span className="value">{invoiceData.cantidad}</span>
                </div>
              )}
              {invoiceData.plazo_entrega && (
                <div className="metadata-detail-row">
                  <span className="label">Plazo entrega:</span>
                  <span className="value">{invoiceData.plazo_entrega}</span>
                </div>
              )}
              {invoiceData.peso_total_kg && (
                <div className="metadata-detail-row">
                  <span className="label">Peso total:</span>
                  <span className="value">{invoiceData.peso_total_kg} kg</span>
                </div>
              )}
              {invoiceData.importe_total && (
                <div className="metadata-detail-row">
                  <span className="label">Importe:</span>
                  <span className="value">{invoiceData.importe_total}€</span>
                </div>
              )}
              {invoiceData.notas && (
                <div className="metadata-detail-row">
                  <span className="label">Notas:</span>
                  <span className="value">{invoiceData.notas}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="query-note">Sin datos de documento disponibles</p>
          )}
        </div>
      </div>

      {/* Modal de imagen */}
      {imageModalOpen && imageBlobUrl && (
        <div className="image-modal-overlay" onClick={() => setImageModalOpen(false)}>
          <div className="image-modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setImageModalOpen(false)}>✕</button>
            <img src={imageBlobUrl} alt={analysis.filename} />
          </div>
        </div>
      )}
    </div>
  );
};

const ComparisonView = ({
  queryFilename,
  queryMetadata,
  queryAnalysis: queryAnalysisProp,
  searchHash,
  queryImageUrl,
  queryDrawingPages,
  results,
  scores,
  topOverall = [],
  onClose,
  onSearchRecent,
  onSearchHistorical,
  onSearchHeuristic,
  heuristicByPage = {},
  stageStatus,
  stageCounts = {},
  isSearching,
  elementsSearchEnabled = false,
  elementsQueryPages = []
}) => {
  const [queryAnalysisState, setQueryAnalysisState] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedQueryPage, setSelectedQueryPage] = useState(null);
  const [showElementsSearch, setShowElementsSearch] = useState(false);
  const [similarityThreshold, setSimilarityThreshold] = useState(null);

  useEffect(() => {
    getAppConfig().then(config => setSimilarityThreshold(config.similarity_threshold)).catch(() => {});
  }, []);

  const hasMultipleQueryPages = queryDrawingPages && queryDrawingPages.length > 1;
  const hasStageButtons = onSearchRecent && onSearchHistorical && stageStatus;

  const heuristicPageKey = selectedQueryPage || 'default';
  const heuristicData = heuristicByPage[heuristicPageKey] || {};
  const heuristicStatus = heuristicData.status || 'idle';
  const heuristicResults = heuristicData.results || [];
  const heuristicScores = heuristicData.scores || {};

  useEffect(() => {
    if (hasMultipleQueryPages && queryDrawingPages?.length > 0) {
      setSelectedQueryPage(queryDrawingPages[0].page_number);
    }
  }, [queryDrawingPages, hasMultipleQueryPages]);

  useEffect(() => {
    if (selectedQueryPage && selectedResult && selectedResult.query_page !== selectedQueryPage) {
      setSelectedResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQueryPage]);

  // Crear un análisis "virtual" para la pieza query con los datos que tenemos
  useEffect(() => {
    if (queryMetadata) {
      const hasFullAnalysis = queryAnalysisProp && Object.keys(queryAnalysisProp).length > 0;

      setQueryAnalysisState({
        hash: 'query',
        filename: queryFilename || 'Tu pieza',
        status: 'completed',
        isQueryPiece: !hasFullAnalysis,
        metadata: queryMetadata,
        result: {
          full_json: hasFullAnalysis ? queryAnalysisProp : {
            TipoPieza: queryMetadata.part_type || 'N/A',
            OperacionesMecanizado: {}
          }
        }
      });
    } else if (queryFilename) {
      setQueryAnalysisState({
        hash: 'query',
        filename: queryFilename,
        status: 'pending',
        isQueryPiece: true,
        metadata: {},
        result: { full_json: { TipoPieza: 'N/A', OperacionesMecanizado: {} } }
      });
    }
  }, [queryMetadata, queryFilename, queryAnalysisProp]);

  // Cargar análisis completo cuando se selecciona un resultado
  useEffect(() => {
    if (selectedResult) {
      setLoading(true);
      getAnalysis(selectedResult.hash)
        .then(analysis => {
          setSelectedAnalysis(analysis);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error loading analysis:', err);
          setLoading(false);
        });
    } else {
      setSelectedAnalysis(null);
    }
  }, [selectedResult]);

  const getScore = (hash) => scores?.[hash] || 0;

  const filteredResults = hasMultipleQueryPages && selectedQueryPage
    ? results.filter(r => r.query_page === selectedQueryPage)
    : results;

  return (
    <div className="comparison-view">
      <div className="comparison-header">
        <h2>Comparar análisis</h2>
        <div className="comparison-header-actions">
          {elementsSearchEnabled && elementsQueryPages.length > 0 && (
            <button onClick={() => setShowElementsSearch(true)} className="btn-elements-search">
              Búsqueda por elementos
            </button>
          )}
          <button onClick={onClose} className="btn-close-comparison">
            Volver a resultados
          </button>
        </div>
      </div>

      {/* Lista de resultados para seleccionar */}
      <div className="comparison-selector">
        {hasMultipleQueryPages && selectedQueryPage && (
          <span className="selector-page-label">Duplicados de página {selectedQueryPage}:</span>
        )}
        <div className="results-groups">
          {/* Grupo: Muy similares */}
          {similarityThreshold !== null && filteredResults.filter(r => getScore(r.hash) >= similarityThreshold).length > 0 && (
            <div className="results-group very-similar">
              <span className="group-title">Muy similares</span>
              <div className="results-chips">
                {filteredResults
                  .filter(r => getScore(r.hash) >= similarityThreshold)
                  .sort((a, b) => getScore(b.hash) - getScore(a.hash))
                  .slice(0, 10)
                  .map((result, idx) => (
                    <button
                      key={`${result.hash}-${idx}`}
                      className={`result-chip ${selectedResult?.hash === result.hash && selectedResult?.query_page === result.query_page && selectedResult?.matched_page === result.matched_page ? 'selected' : ''}`}
                      onClick={() => setSelectedResult(result)}
                    >
                      <span className="chip-name">{result.filename}</span>
                      {hasMultipleQueryPages && result.query_page && (
                        <span className="chip-query-page">Pág. {result.query_page}</span>
                      )}
                    </button>
                  ))}
              </div>
            </div>
          )}
          {/* Grupo: Más cercanos (solo tras completar stage 3, hasta completar 10 en total) */}
          {similarityThreshold !== null && stageStatus?.stage3 === 'completed' && filteredResults.filter(r => getScore(r.hash) < similarityThreshold).length > 0 && filteredResults.filter(r => getScore(r.hash) >= similarityThreshold).length < 10 && (
            <div className="results-group related">
              <span className="group-title">Más cercanos</span>
              <div className="results-chips">
                {filteredResults
                  .filter(r => getScore(r.hash) < similarityThreshold)
                  .sort((a, b) => getScore(b.hash) - getScore(a.hash))
                  .slice(0, 10 - Math.min(filteredResults.filter(r => getScore(r.hash) >= similarityThreshold).length, 10))
                  .map((result, idx) => (
                    <button
                      key={`${result.hash}-${idx}`}
                      className={`result-chip no-score ${selectedResult?.hash === result.hash && selectedResult?.query_page === result.query_page && selectedResult?.matched_page === result.matched_page ? 'selected' : ''}`}
                      onClick={() => setSelectedResult(result)}
                    >
                      <span className="chip-name">{result.filename}</span>
                      {hasMultipleQueryPages && result.query_page && (
                        <span className="chip-query-page">Pág. {result.query_page}</span>
                      )}
                    </button>
                  ))}
              </div>
            </div>
          )}
          {/* Grupo: Otras piezas parecidas (top 10 de Milvus que no aparecen como muy similares ni más cercanos) */}
          {stageStatus?.stage3 === 'completed' && topOverall.length > 0 && (() => {
            const visibleHashes = new Set(filteredResults.map(r => r.hash));
            const rejected = topOverall
              .filter(item => !visibleHashes.has(item.analysis_hash))
              .filter(item => !selectedQueryPage || item.query_page === selectedQueryPage || !item.query_page)
              .slice(0, 10);
            if (rejected.length === 0) return null;
            return (
              <div className="results-group rejected">
                <span className="group-title">Otras piezas parecidas</span>
                <div className="results-chips">
                  {rejected.map((item, idx) => (
                    <button
                      key={`rejected-${item.analysis_hash}-${idx}`}
                      className={`result-chip no-score ${selectedResult?.hash === item.analysis_hash ? 'selected' : ''}`}
                      onClick={() => setSelectedResult({ hash: item.analysis_hash, filename: item.filename, matched_page: item.matched_page, query_page: item.query_page })}
                    >
                      <span className="chip-name">{item.filename}</span>
                      {hasMultipleQueryPages && item.query_page && (
                        <span className="chip-query-page">Pág. {item.query_page}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
          {/* Sin muy similares - hint para ampliar búsqueda */}
          {similarityThreshold !== null && filteredResults.filter(r => getScore(r.hash) >= similarityThreshold).length === 0 && (
            <div className="no-results-message">
              {filteredResults.length === 0
                ? 'No se encontraron piezas similares en esta búsqueda.'
                : 'No se encontraron piezas muy similares en esta búsqueda.'
              }
              {stageStatus?.stage1 === 'completed' && stageStatus?.stage2 === 'pending' && (
                <span> Puedes ampliar la búsqueda en los análisis más recientes.</span>
              )}
              {stageStatus?.stage2 === 'completed' && stageStatus?.stage3 === 'pending' && (
                <span> Puedes buscar en toda la colección (puede tardar un poco más).</span>
              )}
            </div>
          )}
        </div>

        {/* Botones de búsqueda por etapas */}
        {hasStageButtons && (
          <div className="stage-buttons">
            {stageStatus.stage1 === 'completed' && (stageStatus.stage2 === 'pending' || stageStatus.stage2 === 'searching') && (
              <button
                onClick={onSearchRecent}
                className="btn-stage"
                disabled={isSearching}
              >
                {stageStatus.stage2 === 'searching' ? (
                  <><span className="btn-spinner"></span> Buscando...</>
                ) : (
                  'Buscar en recientes (2025-2026)'
                )}
              </button>
            )}
            {stageStatus.stage2 === 'completed' && (stageStatus.stage3 === 'pending' || stageStatus.stage3 === 'searching' || stageStatus.stage3 === 'validating') && (
              <button
                onClick={() => onSearchHistorical(filteredResults.filter(r => getScore(r.hash) >= similarityThreshold).length)}
                className="btn-stage"
                disabled={isSearching}
              >
                {stageStatus.stage3 === 'validating' ? (
                  <><span className="btn-spinner"></span> Validando resultados...</>
                ) : stageStatus.stage3 === 'searching' ? (
                  <><span className="btn-spinner"></span> Buscando...</>
                ) : (
                  'Buscar en histórico'
                )}
              </button>
            )}
            {stageStatus.stage3 === 'completed' && (
              <span className="stage-complete-message">Búsqueda completa</span>
            )}
            {(stageCounts[1] || stageCounts[2] || stageCounts[3]) > 0 && (
              <div className="stage-counts">
                {stageCounts[1] > 0 && <span>Carpeta: {stageCounts[1]}</span>}
                {stageCounts[2] > 0 && <span>Recientes: {stageCounts[2]}</span>}
                {stageCounts[3] > 0 && <span>Histórico: {stageCounts[3]}</span>}
              </div>
            )}
          </div>
        )}

        {/* Búsqueda heurística */}
        {stageStatus?.stage3 === 'completed' && onSearchHeuristic && (
          <div className="heuristic-section">
            {(!heuristicStatus || heuristicStatus === 'idle') && (
              <button onClick={() => onSearchHeuristic(selectedQueryPage)} className="btn-stage btn-heuristic">
                Búsqueda heurística
              </button>
            )}
            {heuristicStatus === 'searching' && (
              <span className="heuristic-status"><span className="btn-spinner"></span> Buscando candidatos...</span>
            )}
            {heuristicStatus === 'validating' && (
              <span className="heuristic-status"><span className="btn-spinner"></span> Validando con IA...</span>
            )}
            {heuristicStatus === 'completed' && (
              heuristicResults.length > 0 ? (
                <div className="results-group heuristic">
                  <span className="group-title">Búsqueda heurística ({heuristicResults.length})</span>
                  <div className="results-chips">
                    {heuristicResults.map((result, idx) => (
                      <button
                        key={`heuristic-${result.hash}-${idx}`}
                        className={`result-chip no-score ${selectedResult?.hash === result.hash && selectedResult?.matched_page === result.matched_page ? 'selected' : ''}`}
                        onClick={() => setSelectedResult(result)}
                      >
                        <span className="chip-name">{result.filename}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <span className="heuristic-no-results">No se encontraron candidatos adicionales</span>
              )
            )}
          </div>
        )}
      </div>

      {/* Paneles lado a lado */}
      <div className="comparison-panels">
        <div className="comparison-panel left">
          {hasMultipleQueryPages ? (
            <div className="multi-query-images">
              <div className="embedded-header" style={{ borderLeftColor: '#1976d2' }}>
                <h3>Tu documento ({queryDrawingPages.length} dibujos)</h3>
                <span className="embedded-filename">{queryFilename}</span>
              </div>
              {queryAnalysisProp?.hash && (
                <div className="embedded-actions">
                  <button onClick={() => window.open(`/analysis/${queryAnalysisProp.hash}`, '_blank')} className="btn-embedded-action" title="Se abre en una pestaña nueva para no perder esta comparativa">
                    Ver análisis completo ↗
                  </button>
                </div>
              )}
              <div className="query-images-grid">
                {queryDrawingPages.map((page) => (
                  <div
                    key={page.page_number}
                    className={`query-image-item ${selectedQueryPage === page.page_number ? 'highlighted' : ''}`}
                    onClick={() => setSelectedQueryPage(page.page_number)}
                  >
                    <div className="query-image-label">Pág. {page.page_number}</div>
                    <img src={page.imageUrl} alt={`Página ${page.page_number}`} />
                  </div>
                ))}
              </div>
              {/* Datos del documento para múltiples páginas */}
              {queryAnalysisProp?.result && (
                <div className="embedded-operations">
                  <h4>Datos del Documento</h4>
                  {(() => {
                    const fullJson = queryAnalysisProp.result?.full_json || queryAnalysisProp.result || {};
                    const invoiceData = fullJson._invoice_data || null;
                    if (!invoiceData) return <p className="query-note">Sin datos de documento disponibles</p>;
                    return (
                      <div className="query-metadata-details">
                        {(invoiceData.numero_documento || invoiceData.numero_pedido) && (
                          <div className="metadata-detail-row">
                            <span className="label">Nº Documento:</span>
                            <span className="value">{invoiceData.numero_documento || invoiceData.numero_pedido}</span>
                          </div>
                        )}
                        {invoiceData.fecha && (
                          <div className="metadata-detail-row">
                            <span className="label">Fecha:</span>
                            <span className="value">{invoiceData.fecha}</span>
                          </div>
                        )}
                        {invoiceData.cliente && (
                          <div className="metadata-detail-row">
                            <span className="label">Cliente:</span>
                            <span className="value">{invoiceData.cliente}</span>
                          </div>
                        )}
                        {invoiceData.proveedor && (
                          <div className="metadata-detail-row">
                            <span className="label">Proveedor:</span>
                            <span className="value">{invoiceData.proveedor}</span>
                          </div>
                        )}
                        {invoiceData.articulo && (
                          <div className="metadata-detail-row">
                            <span className="label">Artículo:</span>
                            <span className="value">{invoiceData.articulo}</span>
                          </div>
                        )}
                        {invoiceData.descripcion && (
                          <div className="metadata-detail-row">
                            <span className="label">Descripción:</span>
                            <span className="value">{invoiceData.descripcion}</span>
                          </div>
                        )}
                        {invoiceData.cantidad && (
                          <div className="metadata-detail-row">
                            <span className="label">Cantidad:</span>
                            <span className="value">{invoiceData.cantidad}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : (
            <EmbeddedPreview
              analysis={queryAnalysisState}
              title="Tu pieza"
              titleColor="#1976d2"
              searchHash={searchHash}
              imageUrl={queryImageUrl}
              onShowPreview={queryAnalysisProp?.hash ? () => window.open(`/analysis/${queryAnalysisProp.hash}`, '_blank') : null}
            />
          )}
        </div>

        <div className="comparison-divider">
          <span>VS</span>
        </div>

        <div className="comparison-panel right">
          {loading ? (
            <div className="embedded-preview empty">
              <p>Cargando análisis...</p>
            </div>
          ) : (
            <EmbeddedPreview
              analysis={selectedAnalysis}
              title={selectedResult ? `${selectedResult.filename}${selectedResult.matched_page ? ` (pág. ${selectedResult.matched_page})` : ''}` : 'Pieza a comparar'}
              titleColor="#f57c00"
              showActions
              onShowPreview={selectedAnalysis ? () => window.open(`/analysis/${selectedAnalysis.hash}`, '_blank') : null}
              matchedPage={selectedResult?.matched_page}
            />
          )}
        </div>
      </div>

      {/* NUBBI-102: modal de búsqueda por elementos (solo cuentas con flag) */}
      {showElementsSearch && (
        <ElementsSearchModal
          queryDrawingPages={elementsQueryPages}
          excludeHash={queryAnalysisProp?.hash || null}
          onClose={() => setShowElementsSearch(false)}
        />
      )}

    </div>
  );
};

export default ComparisonView;
